/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import Flatbush from 'flatbush';

type RowObj = {
  taskObjs: {
    overallStartTime: number;
    overallEndTime: number;
    y: number;
    height: number;
  }[];
};

type TimePoint = { time: number; y: number };

type SpatialIndex = {
  tasks: Flatbush;
};

/**
 * Gets the task spatial index.
 * @param rowObjs The row layout objects (for the entier dataset).
 * @returns The task spatial index.
 */
const getTasksSpatialIndex = (rowObjs: RowObj[]) => {
  const numTasks = rowObjs.map((r) => r.taskObjs.length).reduce((a, b) => a + b, 0);
  const index = new Flatbush(numTasks);
  rowObjs.forEach((r) =>
    r.taskObjs.forEach(({ overallStartTime, overallEndTime, y, height }) => {
      index.add(overallStartTime, y, overallEndTime, y + height);
    })
  );
  index.finish();
  return index;
};

/**
 * Gets all spatial indices of the component.
 * @param rowObjs The row layout objects (for the entier dataset).
 * @returns The spatial indices.
 */
const getSpatialIndex = (rowObjs: RowObj[]): SpatialIndex => {
  // TODO: add more spatial indices for non task bar stuff
  return {
    tasks: getTasksSpatialIndex(rowObjs)
  };
};

/**
 * Returns the target task that overlaps with the given point.
 * @param rowObjs The row layout objects (for the entier dataset).
 * @param index The spatial index to query.
 * @param param2 The point.
 * @returns The target task.
 */
const queryTasksSpatialIndex = (
  rowObjs: RowObj[],
  index: SpatialIndex['tasks'],
  { time, y }: TimePoint
) => {
  const found = index.search(time, y, time, y);
  if (found.length === 0) return;

  // TODO: Handle task overlaps. Not a problem yet for V1.
  // Instead of depending on order gaurantees from Flatbush (there might not be any), we should
  // just check chronological task order and overlap behavior (when we support that API)
  // to figure out the topmost task in the search result.
  let flatIndex = found[0];

  // TODO: improve this with binary search
  for (let i = 0; i < rowObjs.length; i++) {
    const rowObj = rowObjs[i];
    for (let j = 0; j < rowObj.taskObjs.length; j++) {
      if (flatIndex === 0) return { rowIndex: i, taskIndex: j };
      flatIndex -= 1;
    }
  }
  return;
};

export { getSpatialIndex, queryTasksSpatialIndex };
